export interface RetryOptions {
  attempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (error: unknown) => boolean;
}

const DEFAULT_SHOULD_RETRY = (error: unknown): boolean => {
  const code = (error as { code?: string })?.code ?? '';
  if (typeof code === 'string' && /network|unavailable|timeout|deadline/i.test(code)) return true;
  const message = String((error as Error)?.message ?? error);
  return /network|fetch|timeout|offline|failed to fetch/i.test(message);
};

export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const attempts = opts.attempts ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 400;
  const maxDelayMs = opts.maxDelayMs ?? 4000;
  const shouldRetry = opts.shouldRetry ?? DEFAULT_SHOULD_RETRY;

  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (i >= attempts - 1 || !shouldRetry(err)) break;
      const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** i);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError;
}
